import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { useAppDispatch } from '../../store/hooks';
import { addWidget } from '../../store/dashboardSlice';
import './AddWidgetModal.css';

interface AddWidgetModalProps {
  categoryId: string;
  onClose: () => void;
}

interface ChartDataRow {
  label: string;
  value: number;
}

const AddWidgetModal: React.FC<AddWidgetModalProps> = ({ categoryId, onClose }) => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [chartData, setChartData] = useState<ChartDataRow[]>([{ label: '', value: 0 }]);

  // Determine chart type based on category
  const getChartTypeForCategory = (catId: string): 'pie' | 'line' | 'bar' | 'text' => {
    switch (catId) {
      case 'cspm':
        return 'pie';
      case 'cwpp':
        return 'line';
      case 'registry':
        return 'bar';
      default:
        return 'text';
    }
  };

  const chartType = getChartTypeForCategory(categoryId);

  const getCategoryDisplayName = (catId: string): string => {
    switch (catId) {
      case 'cspm':
        return 'CSPM Executive Dashboard';
      case 'cwpp':
        return 'CWPP Dashboard';
      case 'registry':
        return 'Registry Scan Dashboard';
      default:
        return 'Dashboard';
    }
  };

  const addChartDataRow = () => {
    setChartData([...chartData, { label: '', value: 0 }]);
  };

  const removeChartDataRow = (index: number) => {
    if (chartData.length > 1) {
      setChartData(chartData.filter((_, i) => i !== index));
    }
  };

  const updateChartDataRow = (index: number, field: 'label' | 'value', value: string | number) => {
    const updatedData = [...chartData];
    if (field === 'label') {
      updatedData[index].label = value as string;
    } else {
      updatedData[index].value = Number(value);
    }
    setChartData(updatedData);
  };

  const handleAdd = () => {
    if (!name.trim()) return;
    
    const validChartData = chartData.filter(row => row.label.trim() && row.value >= 0);
    
    dispatch(addWidget({ 
      categoryId, 
      name, 
      text, 
      type: chartType,
      chartData: validChartData.length > 0 ? {
        labels: validChartData.map(row => row.label),
        values: validChartData.map(row => row.value)
      } : undefined
    }));
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">
            <h3>Add Widget to {getCategoryDisplayName(categoryId)}</h3>
            <div className="chart-type-badge">
              {chartType === 'bar' ? 'PROGRESS BARS' : `${chartType.toUpperCase()} CHART`}
            </div>
          </div>
          <button className="close-button" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="form-section">
            <h4>Widget Information</h4>
            <div className="form-row">
              <label>Widget Name *</label>
              <input 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                placeholder="Enter widget name"
                className="form-input"
              />
            </div>
            <div className="form-row">
              <label>Description</label>
              <textarea 
                value={text} 
                onChange={(e) => setText(e.target.value)} 
                placeholder="Enter widget description"
                className="form-textarea"
                rows={2}
              />
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <h4>{chartType === 'bar' ? 'Progress Data (Progress Bars)' : `Chart Data (${chartType.toUpperCase()} Chart)`}</h4>
              <button 
                type="button" 
                onClick={addChartDataRow} 
                className="add-data-button"
              >
                <Plus size={16} />
                Add Data Point
              </button>
            </div>
            
            <div className="chart-data-container">
              {chartData.map((row, index) => (
                <div key={index} className="data-row">
                  <input 
                    type="text"
                    value={row.label}
                    onChange={(e) => updateChartDataRow(index, 'label', e.target.value)}
                    placeholder={chartType === 'bar' ? 'Category (e.g., Critical, High, etc.)' : chartType === 'pie' ? 'Segment (e.g., Failed, Passed, etc.)' : 'Data Point (e.g., Jan, Feb, etc.)'}
                    className="data-input label-input"
                  />
                  <input 
                    type="number"
                    value={row.value === 0 ? '' : row.value}
                    onChange={(e) => updateChartDataRow(index, 'value', e.target.value || '0')}
                    placeholder="Enter value"
                    min="0"
                    className="data-input value-input"
                  />
                  {chartData.length > 1 && (
                    <button 
                      type="button"
                      onClick={() => removeChartDataRow(index)}
                      className="remove-data-button"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            <div className="chart-preview">
              <p className="preview-text">
                {chartType === 'bar' ? '📊' : chartType === 'pie' ? '🥧' : '📈'} Preview: Your {chartType === 'bar' ? 'progress bars' : `${chartType} chart`} will display {chartData.filter(row => row.label.trim() && row.value >= 0).length} data points
              </p>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn secondary" onClick={onClose}>Cancel</button>
          <button 
            className="btn primary" 
            onClick={handleAdd}
            disabled={!name.trim()}
          >
            Create Widget
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddWidgetModal;
